import React, { useContext, useState } from "react";
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  Hidden,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";
import LocalAtmOutlinedIcon from "@mui/icons-material/LocalAtmOutlined";
import { bakeStreetLogo } from "../constants";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import { UserContext } from "../../context/UserContext";
import { getDeviceToken } from "../../firebase";
import bkstApi from "../../api";
import LiveHelpOutlinedIcon from "@mui/icons-material/LiveHelpOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate } from "react-router";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CreateMenu from "./CreateMenu";
import StarHalfOutlinedIcon from "@mui/icons-material/StarHalfOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import NewReleasesOutlinedIcon from "@mui/icons-material/NewReleasesOutlined";
import {
  isPOSMode,
  isKDSMode,
  isInventoryControlEnabled,
  isCashAdvanceOptionAvailable,
} from "../util";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { canAccessPath } from "../access-control";

export default function ResponsiveDrawer(props) {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [showPlanningMenu, setShowPlanningMenu] = useState(false);
  const [showReportsMenu, setShowReportsMenu] = useState(false);
  const [showSalesReportMenu, setShowSalesReportMenu] = useState(false);
  const [showPaymentsMenu, setShowPaymentsMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);

  const container = window !== undefined ? () => document.body : undefined;

  const enablePushNotifications = async (e) => {
    e.preventDefault();
    const token = await getDeviceToken();
    if (token) {
      bkstApi.post(`/fcm`, { token });
    }
  };

  const handleReportsMenuClick = (e) => {
    e.stopPropagation();
    setShowReportsMenu(!showReportsMenu);
  };

  const handleSalesReportMenuClick = (e) => {
    e.stopPropagation();
    setShowSalesReportMenu(!showSalesReportMenu);
  };

  const handlePaymentsMenuClick = (e) => {
    e.stopPropagation();
    setShowPaymentsMenu(!showPaymentsMenu);
  };

  const handlePlanningMenuClick = (e) => {
    e.stopPropagation();
    setShowPlanningMenu(!showPlanningMenu);
  };

  const handleSettingsMenuClick = (e) => {
    e.stopPropagation();
    setShowSettingsMenu(!showSettingsMenu);
  };

  let home = "/dashboard";
  if (isKDSMode(user)) {
    home = "/production/tracker";
  } else if (user?.role === "clerk") {
    home = "/";
  }

  const isOwner = user.role === "baker" || user.role === "owner";
  const isManager = user.role === "rep" || user.role === "manager";
  const isKitchen = user.role === "kitchen" || user.role === "kitchen leader";
  const isClerk = user.role === "clerk";
  const showOwnerMenu = isOwner || isManager;
  const include = (path) => {
    if (!canAccessPath(user, path)) {
      return false;
    }
    if (showOwnerMenu) {
      return true;
    }
    if (isClerk && (path === "/create" || path === "/order")) {
      return false;
    }
    if (isKitchen && path === "/calendar") {
      return false;
    }
    // KDS home is Production Tracker — no separate Orders menu entry needed
    if (isKDSMode(user) && path === "/order") {
      return false;
    }
    return true;
  };
  const canViewPaymentsReports =
    include("/report/cash-drawer-activity") ||
    include("/report/payout") ||
    include("/report/refunds") ||
    include("/report/tips") ||
    include("/report/transaction");
  const canViewSalesReports =
    include("/report/item-sales") ||
    include("/report/performance") ||
    include("/report/revenue-recognition") ||
    include("/report/sales") ||
    include("/report/sales-summary");
  const canViewReports = include("/report/end-of-day") || canViewPaymentsReports || canViewSalesReports;
  const canPlan =
    include("/calendar") || include("/planning/fulfillment") || include("/planner/production");
  const canViewCatalog =
    include("/catalog/standard") ||
    (include("/inventory") && isInventoryControlEnabled(user.acctId));
  const canViewSettings =
    include("/settings/staff") ||
    include("/settings/store-location") ||
    include("/settings/photos") ||
    include("/plan");
  const canViewCashAdvance =
    include("/cash-advance") && isCashAdvanceOptionAvailable(user.acctId);
  const hasExtras =
    !showOwnerMenu &&
    (include("/create") ||
      include("/quote") ||
      include("/order") ||
      canViewCatalog ||
      canPlan ||
      canViewReports ||
      canViewCashAdvance ||
      canViewSettings ||
      include("/reviews"));

  const renderAppMenu = (chrome) => (
    <List sx={{ padding: 0 }}>
      {!chrome && !(isKitchen && canAccessPath(user, "/calendar")) && (
        <Divider variant="middle" style={{ marginTop: "15px", marginBottom: "15px" }} />
      )}
      {include("/create") && (
        <Box px={2} pb={2}>
          <CreateMenu />
        </Box>
      )}
      {include("/quote") && (
        <ListItemButton component={RouterLink} to="/quote">
          <ListItemIcon>
            <LocalAtmOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary={`Quotes`} />
        </ListItemButton>
      )}
      {include("/order") && (
        <ListItemButton component={RouterLink} to="/order">
          <ListItemIcon>
            <LocalMallOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary="Orders" />
        </ListItemButton>
      )}
      {chrome && <Divider variant="middle" style={{ marginTop: "15px", marginBottom: "15px" }} />}
      {(chrome || canViewCatalog) && (
        <Box>
          {include("/catalog/standard") && (
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/catalog/standard">
                <ListItemIcon>
                  <ShoppingCartOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary={`Catalog`} />
              </ListItemButton>
            </ListItem>
          )}
          {include("/inventory") && isInventoryControlEnabled(user.acctId) && (
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/inventory">
                <ListItemIcon>
                  <Inventory2OutlinedIcon />
                </ListItemIcon>
                <ListItemText primary={`Inventory`} />
              </ListItemButton>
            </ListItem>
          )}
          {chrome && (
            <Divider variant="middle" style={{ marginTop: "15px", marginBottom: "15px" }} />
          )}
        </Box>
      )}
      {canPlan && (
        <Box>
          <ListItem onClick={handlePlanningMenuClick} disablePadding>
            <ListItemButton>
              <ListItemIcon>
                <AssignmentOutlinedIcon />
              </ListItemIcon>
              <ListItemText primary="Planner" />
              {showPlanningMenu ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={showPlanningMenu} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {include("/calendar") && (
                <ListItem disablePadding sx={{ paddingLeft: "1rem" }}>
                  <ListItemButton component={RouterLink} to="/calendar">
                    <ListItemIcon>
                      <ChevronRightOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary={`Calendar`} />
                  </ListItemButton>
                </ListItem>
              )}
              {include("/planning/fulfillment") && (
                <ListItem disablePadding sx={{ paddingLeft: "1rem" }}>
                  <ListItemButton component={RouterLink} to="/planning/fulfillment">
                    <ListItemIcon>
                      <ChevronRightOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary={`Fulfillment`} />
                  </ListItemButton>
                </ListItem>
              )}
              {include("/planner/production") && (
                <ListItem disablePadding sx={{ paddingLeft: "1rem" }}>
                  <ListItemButton component={RouterLink} to="/planner/production">
                    <ListItemIcon>
                      <ChevronRightOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary={`Production`} />
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Collapse>
        </Box>
      )}
      {canViewReports && (
        <Box>
          <ListItem onClick={handleReportsMenuClick} disablePadding>
            <ListItemButton>
              <ListItemIcon>
                <AssessmentOutlinedIcon />
              </ListItemIcon>
              <ListItemText primary="Reports" />
              {showReportsMenu ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={showReportsMenu} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {include("/report/end-of-day") && (
                <ListItem disablePadding sx={{ paddingLeft: "1rem" }}>
                  <ListItemButton component={RouterLink} to="/report/end-of-day">
                    <ListItemIcon>
                      <ChevronRightOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary="End of Day" />
                  </ListItemButton>
                </ListItem>
              )}
              {canViewPaymentsReports && (
                <ListItem disablePadding sx={{ paddingLeft: "1rem" }}>
                  <ListItemButton onClick={handlePaymentsMenuClick}>
                    <ListItemIcon>
                      <ChevronRightOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary="Payments" />
                    {showPaymentsMenu ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
              )}
              <Collapse
                in={canViewPaymentsReports && showPaymentsMenu}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {include("/report/cash-drawer-activity") && (
                    <ListItem disablePadding sx={{ paddingLeft: "2rem" }}>
                      <ListItemButton component={RouterLink} to="/report/cash-drawer-activity">
                        <ListItemIcon>
                          <ChevronRightOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Cash Drawer Activity" />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {include("/report/payout") && (
                    <ListItem disablePadding sx={{ paddingLeft: "2rem" }}>
                      <ListItemButton component={RouterLink} to="/report/payout">
                        <ListItemIcon>
                          <ChevronRightOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Payouts" />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {include("/report/refunds") && (
                    <ListItem disablePadding sx={{ paddingLeft: "2rem" }}>
                      <ListItemButton component={RouterLink} to="/report/refunds">
                        <ListItemIcon>
                          <ChevronRightOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Refunds" />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {include("/report/tips") && (
                    <ListItem disablePadding sx={{ paddingLeft: "2rem" }}>
                      <ListItemButton component={RouterLink} to="/report/tips">
                        <ListItemIcon>
                          <ChevronRightOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Tips" />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {include("/report/transaction") && (
                    <ListItem disablePadding sx={{ paddingLeft: "2rem" }}>
                      <ListItemButton component={RouterLink} to="/report/transaction">
                        <ListItemIcon>
                          <ChevronRightOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Transactions" />
                      </ListItemButton>
                    </ListItem>
                  )}
                </List>
              </Collapse>
              {canViewSalesReports && (
                <ListItem disablePadding sx={{ paddingLeft: "1rem" }}>
                  <ListItemButton onClick={handleSalesReportMenuClick}>
                    <ListItemIcon>
                      <ChevronRightOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary="Sales" />
                    {showSalesReportMenu ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
              )}
              <Collapse
                in={canViewSalesReports && showSalesReportMenu}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {include("/report/item-sales") && (
                    <ListItem disablePadding sx={{ paddingLeft: "2rem" }}>
                      <ListItemButton component={RouterLink} to="/report/item-sales">
                        <ListItemIcon>
                          <ChevronRightOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Item Sales" />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {include("/report/performance") &&
                    user.acctId === "m7w2nldh2VlpJijvH2tC" && (
                      <ListItem disablePadding sx={{ paddingLeft: "2rem" }}>
                        <ListItemButton component={RouterLink} to="/report/performance">
                          <ListItemIcon>
                            <ChevronRightOutlinedIcon />
                          </ListItemIcon>
                          <ListItemText primary="Performance" />
                        </ListItemButton>
                      </ListItem>
                    )}
                  {include("/report/revenue-recognition") && (
                    <ListItem disablePadding sx={{ paddingLeft: "2rem" }}>
                      <ListItemButton component={RouterLink} to="/report/revenue-recognition">
                        <ListItemIcon>
                          <ChevronRightOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Revenue Recognition" />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {include("/report/sales") && (
                    <ListItem disablePadding sx={{ paddingLeft: "2rem" }}>
                      <ListItemButton component={RouterLink} to="/report/sales">
                        <ListItemIcon>
                          <ChevronRightOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Sales Activity" />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {include("/report/sales-summary") && (
                    <ListItem disablePadding sx={{ paddingLeft: "2rem" }}>
                      <ListItemButton component={RouterLink} to="/report/sales-summary">
                        <ListItemIcon>
                          <ChevronRightOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Sales Summary" />
                      </ListItemButton>
                    </ListItem>
                  )}
                </List>
              </Collapse>
            </List>
          </Collapse>
          {canViewCashAdvance && (
            <Box>
              <Divider variant="middle" style={{ marginTop: "15px", marginBottom: "15px" }} />
              <Box>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/loan">
                    <ListItemIcon>
                      <AccountBalanceOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary={`Working Capital`} />
                  </ListItemButton>
                </ListItem>
              </Box>
            </Box>
          )}
        </Box>
      )}
      {!canViewReports && canViewCashAdvance && (
        <Box>
          <Divider variant="middle" style={{ marginTop: "15px", marginBottom: "15px" }} />
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/loan">
              <ListItemIcon>
                <AccountBalanceOutlinedIcon />
              </ListItemIcon>
              <ListItemText primary={`Working Capital`} />
            </ListItemButton>
          </ListItem>
        </Box>
      )}
      {(chrome || canViewSettings || include("/reviews")) && (
        <Box>
          {canViewSettings && (
            <>
              <ListItem onClick={handleSettingsMenuClick} disablePadding>
                <ListItemButton>
                  <ListItemIcon>
                    <SettingsOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText primary="Settings" />
                  {showSettingsMenu ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={showSettingsMenu} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {include("/settings/staff") && (
                    <ListItem disablePadding sx={{ paddingLeft: "1rem" }}>
                      <ListItemButton component={RouterLink} to="/settings/staff">
                        <ListItemIcon>
                          <Groups2OutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary={`Staff`} />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {include("/settings/store-location") && (
                    <ListItem disablePadding sx={{ paddingLeft: "1rem" }}>
                      <ListItemButton component={RouterLink} to="/settings/store-location">
                        <ListItemIcon>
                          <StorefrontOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary={`Stores`} />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {include("/settings/photos") && (
                    <ListItem disablePadding sx={{ paddingLeft: "1rem" }}>
                      <ListItemButton component={RouterLink} to="/settings/photos">
                        <ListItemIcon>
                          <PhotoLibraryOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary={`Gallery`} />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {include("/plan") && (
                    <ListItem disablePadding sx={{ paddingLeft: "1rem" }}>
                      <ListItemButton component={RouterLink} to="/plan">
                        <ListItemIcon>
                          <CardMembershipOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary={`Plan`} />
                      </ListItemButton>
                    </ListItem>
                  )}
                </List>
              </Collapse>
            </>
          )}
          {include("/reviews") && (
            <Box>
              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/reviews">
                  <ListItemIcon>
                    <StarHalfOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText primary={`Reviews`} />
                </ListItemButton>
              </ListItem>
            </Box>
          )}
          {chrome && <Divider variant="middle" style={{ marginTop: "15px" }} />}
        </Box>
      )}
    </List>
  );

  const drawer = (
    <div>
      <div>
        <Box sx={{ height: "65px", display: "flex", gap: "5px", alignItems: "center" }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIosNewIcon />
          </IconButton>
          <Link component={RouterLink} to={home}>
            <img alt="logo" style={{ width: "110px" }} src={bakeStreetLogo}></img>
          </Link>
        </Box>
      </div>
      {isKDSMode(user) ? (
        // Same short kitchen menu for every role on a KDS device.
        <List>
          {canAccessPath(user, "/calendar") && (
            <Box>
              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/calendar">
                  <ListItemIcon>
                    <CalendarMonthOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText primary={`Calendar`} />
                </ListItemButton>
              </ListItem>
              <Divider variant="middle" style={{ marginTop: "15px", marginBottom: "15px" }} />
            </Box>
          )}
        </List>
      ) : (
        <>
          {showOwnerMenu && renderAppMenu(true)}
          {(isKitchen || isClerk) && (
            <List>
              {isKitchen && canAccessPath(user, "/calendar") && (
                <Box>
                  <ListItem disablePadding>
                    <ListItemButton component={RouterLink} to="/calendar">
                      <ListItemIcon>
                        <CalendarMonthOutlinedIcon />
                      </ListItemIcon>
                      <ListItemText primary={`Calendar`} />
                    </ListItemButton>
                  </ListItem>
                  <Divider variant="middle" style={{ marginTop: "15px", marginBottom: "15px" }} />
                </Box>
              )}
              {isClerk && (
                <Box>
                  {canAccessPath(user, "/create") && (
                    <Box px={2} pb={2}>
                      <CreateMenu />
                    </Box>
                  )}
                  {canAccessPath(user, "/order") && (
                    <ListItem disablePadding>
                      <ListItemButton component={RouterLink} to="/order">
                        <ListItemIcon>
                          <LocalMallOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Orders" />
                      </ListItemButton>
                    </ListItem>
                  )}
                </Box>
              )}
            </List>
          )}
          {hasExtras && renderAppMenu(false)}
        </>
      )}
      <List>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/release-notes">
            <ListItemIcon>
              <NewReleasesOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary={`Release Notes`} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              window.$zoho.salesiq.visitor.name(
                `${user.profile?.bizName} ${user.firstName ? `: ${user.firstName} ${user.lastName || ""}` : ""}`
              );
              window.$zoho.salesiq.visitor.info({ appVersion: process.env.REACT_APP_VERSION });
              window.$zoho.salesiq.chat.waittime(600);
              window.$zoho.salesiq.floatwindow.visible("show");
            }}
          >
            <ListItemIcon>
              <LiveHelpOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary={`Help`} />
          </ListItemButton>
        </ListItem>
      </List>
      <Box p={4} align="center" sx={{ borderTop: "1px solid #EEE" }}>
        <Box>
          <Typography variant="caption" color="textSecondary" onClick={enablePushNotifications}>
            {process.env.REACT_APP_VERSION}
          </Typography>
        </Box>
        <Typography variant="caption" color="textSecondary" onClick={enablePushNotifications}>
          App Version
        </Typography>
      </Box>
    </div>
  );

  // collapsed menu for kitchen staff
  if (isPOSMode(user) || isKDSMode(user)) {
    return (
      <nav aria-label="main-menu">
        <Drawer
          container={container}
          variant="temporary"
          anchor={"left"}
          open={props.open}
          onClose={props.toggle}
          onClick={props.toggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            width: "240px",
            flexShrink: 0,
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: "240px" },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    );
  } else {
    return (
      <nav aria-label="main-menu">
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Hidden lgUp implementation="css">
          <Drawer
            container={container}
            variant="temporary"
            anchor={"left"}
            open={props.open}
            onClose={props.toggle}
            onClick={props.toggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              width: "240px",
              flexShrink: 0,
              "& .MuiDrawer-paper": { boxSizing: "border-box", width: "240px" },
            }}
          >
            {drawer}
          </Drawer>
        </Hidden>
        <Hidden lgDown implementation="css">
          <Drawer
            variant="permanent"
            open
            sx={{
              width: "240px",
              flexShrink: 0,
              "& .MuiDrawer-paper": { boxSizing: "border-box", width: "240px" },
              "& a": { color: "inherit" },
            }}
          >
            {drawer}
          </Drawer>
        </Hidden>
      </nav>
    );
  }
}
